import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	DEFAULT_FORGE_BANNER_PRESET_HEIGHT_CONFIG,
	getForgeBannerPresetHeightConfig,
} from '../forgePageBannerPresetHeight.ts';

type UseForgePageBannerPresetHeightResult = {
	presetHeight: number | undefined;
	onIframeLoad: () => void;
};

export const useForgePageBannerPresetHeight = (
	shouldPresetHeight: boolean,
): UseForgePageBannerPresetHeightResult => {
	// The caller enables this only for a single Custom UI page-banner app. Memoize the
	// parsed configuration so it stays consistent for this banner's lifecycle.
	const { presetHeight, removalDelay, maxDelay } = useMemo(
		() =>
			shouldPresetHeight
				? getForgeBannerPresetHeightConfig()
				: DEFAULT_FORGE_BANNER_PRESET_HEIGHT_CONFIG,
		[shouldPresetHeight],
	);
	// Reserve height while the Custom UI iframe initializes to avoid a layout shift.
	const [isPresetHeightApplied, setIsPresetHeightApplied] = useState(
		shouldPresetHeight && presetHeight > 5,
	);
	const iframeLoadTimeoutId = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const appLoadTimeoutId = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const removePresetHeight = useCallback(() => {
		setIsPresetHeightApplied(false);
	}, []);

	useEffect(() => {
		if (!isPresetHeightApplied) {
			return;
		}

		// Remove the preset height even when the iframe never loads.
		appLoadTimeoutId.current = setTimeout(removePresetHeight, maxDelay);

		return () => {
			if (iframeLoadTimeoutId.current !== undefined) {
				clearTimeout(iframeLoadTimeoutId.current);
			}
			if (appLoadTimeoutId.current !== undefined) {
				clearTimeout(appLoadTimeoutId.current);
			}
		};
	}, [isPresetHeightApplied, maxDelay, removePresetHeight]);

	const onIframeLoad = useCallback(() => {
		if (!isPresetHeightApplied) {
			return;
		}

		if (appLoadTimeoutId.current !== undefined) {
			clearTimeout(appLoadTimeoutId.current);
		}
		// Once the iframe is ready, keep the preset briefly, then let the banner's
		// measured height take over.
		iframeLoadTimeoutId.current = setTimeout(removePresetHeight, removalDelay);
	}, [isPresetHeightApplied, removalDelay, removePresetHeight]);

	return {
		presetHeight: isPresetHeightApplied ? presetHeight : undefined,
		onIframeLoad,
	};
};
